import type { FeedbackContext } from "@edu/shared";

/**
 * Кольцевой буфер последних отказов API (раздел 6.4
 * `docs/feedback-notes-design.md`).
 *
 * Зачем он вообще есть. Виджет заметок выигрывает у мессенджера ровно двумя
 * вещами, и вторая это техника, которую человек не напишет никогда: маршрут,
 * версия бандла, состояние сети и последние отказы API. Заметка «тут ничего не
 * грузится» без трёх последних кодов отказа чинится гаданием, с ними это
 * обычно одна строка в логе сервера.
 *
 * Почему модульный слой, а не состояние React. Отказ случается в `lib/api.ts`,
 * то есть вне дерева компонентов и часто раньше, чем смонтирован хоть один
 * экран. Кроме того, буфер обязан пережить размонтирование комнаты урока: при
 * разрыве LiveKit комната уходит с маршрута, а заметку про этот разрыв пишут
 * уже после, и отказы, случившиеся до ухода, нужны именно тогда.
 *
 * ⚠️ **Пишет сюда `errorOf()` в `lib/api.ts`, а не `request()`.** `errorOf`
 * зовут ещё из трёх мест помимо `request`, и два из них (загрузка материала и
 * превью) это ровно те отказы, про которые преподаватель пишет чаще всего.
 * Разбор в шапке `errorOf`.
 *
 * ⚠️ **Путь сюда приходит уже замаскированным** (`maskApiPath` из
 * `@edu/shared`). Сырых путей буфер не принимает и маскировать сам не пытается:
 * путь погашения ссылки это `/v1/invites/<код>/redeem`, а код приглашения это
 * секрет, дающий доступ к паре, и заметка живёт в базе 180 суток. Одна точка
 * маскировки на два места применения это правило раздела 3.2 проекта, и
 * дублировать её здесь значило бы завести вторую, которая однажды разойдётся с
 * первой.
 *
 * ⚠️ **`window.onerror` и `unhandledrejection` сюда не подключены намеренно.**
 * В буфер попадали бы исключения расширений браузера и стектрейсы с путями к
 * чужим файлам. Отказ API это событие с известной формой, и его достаточно.
 */

/** Что уезжает в снимок: элементы `context.fails` из контракта. */
type Fails = NonNullable<FeedbackContext["fails"]>;

/**
 * Три, потому что три стоит в контракте (`.max(3)`), а в контракте три, потому
 * что бюджет тела запроса это 16 КБ (раздел 3.4 проекта): соединение в режиме
 * белого списка рвётся примерно там, и виджет для жалоб на плохую сеть обязан
 * работать на плохой сети.
 */
const KEEP = 3;

/** Потолки контракта. Держим их здесь, чтобы отказ не портил заметку целиком. */
const CODE_MAX = 48;
const PATH_MAX = 120;
const AGO_MAX_MS = 2_592_000_000;

interface Fail {
  code: string;
  status: number;
  path: string;
  /** Момент отказа. В снимок уезжает `ago`, потому что часы у всех разные. */
  at: number;
}

let ring: Fail[] = [];

/**
 * Записать отказ. Зовётся из `errorOf()` на каждый неуспешный ответ.
 *
 * ⚠️ Строки чистятся и подрезаются ЗДЕСЬ, а не на отправке, и это защита
 * заметки, а не гигиена. Схема контракта отвергает управляющие символы и строки
 * сверх потолка, отказ схемы это `400`, а `400` по классификации очереди
 * **постоянная** неудача. То есть один странный код отказа от сервера (или
 * заголовок, собранный не нами) мог бы навсегда похоронить заметку человека,
 * который вообще писал не про это.
 */
export function recordFail(status: number, code: string, maskedPath: string): void {
  // Телеметрия отсеивается и на стороне `lib/api.ts`, но правило принадлежит
  // буферу: ICE-события шлются с урока постоянно, и на плохой сети они забили
  // бы все три места собственным шумом ровно к моменту, когда откроют окно
  if (maskedPath.startsWith("/v1/telemetry")) return;

  ring.push({
    // Ноль это «до сервера не дошло»: сетевой отказ, а не ответ. Контракт
    // принимает 0..999, всё остальное было бы отказом схемы
    status: Number.isInteger(status) && status >= 0 && status <= 999 ? status : 0,
    code: printable(code, CODE_MAX),
    path: printable(maskedPath, PATH_MAX),
    at: Date.now(),
  });
  if (ring.length > KEEP) ring = ring.slice(-KEEP);
}

/**
 * Что положить в снимок. Возраст считается в момент снятия снимка, а не в
 * момент отказа: заметка может пролежать в очереди сутки, и «две минуты назад»
 * в ней означало бы две минуты до отправки, а не до события.
 *
 * ⚠️ `ago` зажат в границы контракта с обеих сторон. Отрицательное значение это
 * не теория: `Date.now()` прыгает назад при синхронизации часов и при выходе
 * ноутбука из сна, а отрицательное число здесь означало бы `400`, то есть
 * потерянную заметку из-за перевода часов.
 */
export function recentFails(): Fails {
  const now = Date.now();
  return ring.map((f) => ({
    code: f.code,
    status: f.status,
    path: f.path,
    ago: Math.min(AGO_MAX_MS, Math.max(0, now - f.at)),
  }));
}

/**
 * Строка, которую примет контракт: без управляющих символов и не длиннее
 * потолка. Управляющие символы вырезаются, а не заменяются: видимого
 * содержимого они не несут, и подставлять вместо них знак значило бы врать о
 * длине пути.
 */
function printable(value: string, max: number): string {
  // Набор ровно тот же, что у `Printable` в `packages/shared/src/feedback.ts`:
  // табуляция, перевод строки и возврат каретки разрешены, остальное нет
  return value.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, "").slice(0, max);
}
